/**
 * Solscan API Service
 * Fetches token transfer data from Solscan for USDP token on Solana
 * 
 * NOTE: Solscan's public API has Cloudflare protection that blocks programmatic access.
 * For production use, you should:
 * 1. Get a Solscan Pro API key from https://solscan.io/apis
 * 2. Use a backend proxy server to make API calls
 * 3. Use alternative providers like Helius, QuickNode, or Alchemy
 */

import { getTokenConfig } from '../config/tokens';

// Solscan API Response Interfaces
export interface SolscanTokenTransfer {
  blockTime: number;
  slot: number;
  txHash: string;
  fee: number;
  status: string;
  from: string;
  to: string;
  tokenAddress: string;
  tokenDecimals: number;
  amount: number;
  flow: string; // 'in' or 'out'
}

export interface SolscanTransferResponse {
  success: boolean;
  data: {
    items: SolscanTokenTransfer[];
    total: number;
  };
}

export interface FormattedSolTransfer {
  id: string;
  txHash: string;
  from: string;
  to: string;
  amount: string;
  tokenSymbol: string;
  tokenName: string;
  tokenLogo: string;
  blockNumber: number;
  timestamp: number;
  gasUsed: string;
  gasPrice: string;
  status: 'confirmed' | 'pending' | 'failed';
  chainId: number;
  networkName: string;
  type: 'transfer';
  slot: number;
}

class SolscanService {
  private static instance: SolscanService;
  private baseUrl = 'https://api.solscan.io';
  private chainId = 9999; // Solana chain ID (custom identifier)
  private usdpTokenAddress = 'USDPSaqUFkY2BNXHenNborN9HGUi5H4XuyTr4PSftq1';
  private cache: Map<string, { data: FormattedSolTransfer[]; timestamp: number }> = new Map();
  private cacheDuration = 30000; // 30 seconds cache
  private lastError: string | null = null;
  private useDemoData = true; // Default to demo data since public API is blocked

  static getInstance(): SolscanService {
    if (!this.instance) {
      this.instance = new SolscanService();
    }
    return this.instance;
  }

  private constructor() {
    console.log('🔧 SolscanService initialized for Solana USDP token:', this.usdpTokenAddress);
    console.log('⚠️ Note: Solscan public API has Cloudflare protection. Using demo data by default.');
    console.log('💡 To use real data, get a Solscan Pro API key from https://solscan.io/apis');
  }

  /**
   * Enable/disable demo data mode
   */
  setUseDemoData(useDemo: boolean): void {
    this.useDemoData = useDemo;
    console.log(`🔄 SolscanService: Demo data mode ${useDemo ? 'enabled' : 'disabled'}`);
  }

  /**
   * Fetch token transfers from Solscan API
   * Uses public endpoint (no API key required for basic usage)
   * NOTE: Public API has Cloudflare protection and may be blocked
   */
  async fetchTokenTransfers(
    tokenAddress: string = this.usdpTokenAddress,
    page: number = 1,
    limit: number = 50,
    sortBy: 'block_time' | 'slot' = 'block_time',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<SolscanTokenTransfer[]> {
    // If demo data mode is enabled, return empty array immediately
    // The getUSDPTransfers method will generate demo data
    if (this.useDemoData) {
      console.log('📊 Solscan: Demo data mode - skipping API call');
      return [];
    }

    const cacheKey = `transfers_${tokenAddress}_${page}_${limit}_${sortBy}_${sortOrder}`;
    
    // Check cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      console.log('📦 Returning cached Solscan data');
      return this.parseTransfersFromCache(cached.data);
    }

    try {
      // Solscan API endpoint for token transfers
      const url = `${this.baseUrl}/token/transfer?address=${tokenAddress}&page=${page}&page_size=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}`;
      
      console.log('🌐 Fetching token transfers from Solscan API...');
      console.log('📍 URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Cloudflare blocked the request. Solscan public API requires browser access or Pro API key.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: SolscanTransferResponse = await response.json();
      console.log('📊 Solscan API Response:', {
        success: data.success,
        total: data.data?.total,
        itemCount: data.data?.items?.length || 0
      });

      if (data.success && data.data && data.data.items) {
        console.log(`✅ Fetched ${data.data.items.length} token transfers from Solscan`);
        this.lastError = null;
        return data.data.items;
      } else {
        const errorMsg = `Solscan API error: ${data.success === false ? 'Request failed' : 'Invalid data format'}`;
        console.error('❌', errorMsg);
        this.lastError = errorMsg;
        return [];
      }
    } catch (error) {
      const errorMsg = `Failed to fetch from Solscan: ${error instanceof Error ? error.message : String(error)}`;
      console.error('❌', errorMsg);
      console.log('💡 Tip: To use real Solana data, get a Solscan Pro API key or use a backend proxy.');
      this.lastError = errorMsg;
      return [];
    }
  }

  /**
   * Parse raw transfers from cached formatted data
   */
  private parseTransfersFromCache(formattedTransfers: FormattedSolTransfer[]): SolscanTokenTransfer[] {
    return formattedTransfers.map(ft => ({
      blockTime: Math.floor(ft.timestamp / 1000),
      slot: ft.slot,
      txHash: ft.txHash,
      fee: 0,
      status: ft.status === 'confirmed' ? 'Success' : 'Failed',
      from: ft.from,
      to: ft.to,
      tokenAddress: this.usdpTokenAddress,
      tokenDecimals: 9, // Solana tokens typically have 9 decimals
      amount: parseFloat(ft.amount),
      flow: 'in'
    }));
  }

  /**
   * Format transfers to match our transaction structure
   */
  formatTransfers(transfers: SolscanTokenTransfer[]): FormattedSolTransfer[] {
    const tokenConfig = getTokenConfig('USDP');
    
    return transfers.map(transfer => {
      const amount = this.formatTokenAmount(transfer.amount.toString(), transfer.tokenDecimals);
      
      return {
        id: `sol_${transfer.txHash}_${transfer.slot}`,
        txHash: transfer.txHash,
        from: transfer.from,
        to: transfer.to,
        amount: amount,
        tokenSymbol: 'USDP',
        tokenName: 'USD.P E-Pay Dollar (Solana)',
        tokenLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
        blockNumber: transfer.slot,
        timestamp: transfer.blockTime * 1000, // Convert to milliseconds
        gasUsed: transfer.fee?.toString() || '0',
        gasPrice: '0.000005', // Solana has fixed low fees (~0.000005 SOL)
        status: transfer.status === 'Success' ? 'confirmed' : 'failed',
        chainId: this.chainId,
        networkName: 'Solana',
        type: 'transfer',
        slot: transfer.slot
      };
    });
  }

  /**
   * Format token amount from raw to readable format
   */
  private formatTokenAmount(value: string, decimals: number): string {
    const amount = parseFloat(value) / Math.pow(10, decimals);
    return amount.toFixed(6);
  }

  /**
   * Get USDP token transfers with caching
   * Falls back to demo data if API fails or demo mode is enabled
   */
  async getUSDPTransfers(page: number = 1, limit: number = 50, forceRefresh: boolean = false): Promise<FormattedSolTransfer[]> {
    const cacheKey = `usdp_sol_${page}_${limit}`;
    
    // Check cache (skip if force refresh)
    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
        console.log('📦 Returning cached Solana USDP transfers');
        return cached.data;
      }
    } else {
      console.log('🔄 Force refresh: bypassing cache');
    }

    // Try to fetch from API
    const rawTransfers = await this.fetchTokenTransfers(
      this.usdpTokenAddress,
      page,
      limit,
      'block_time',
      'desc'
    );
    
    let formattedTransfers: FormattedSolTransfer[] = [];
    
    if (rawTransfers.length > 0) {
      // Use real API data
      formattedTransfers = this.formatTransfers(rawTransfers);
      console.log(`✅ Using ${formattedTransfers.length} real transfers from Solscan API`);
    } else if (this.useDemoData) {
      // Fall back to demo data
      formattedTransfers = this.generateDemoTransfers(limit);
      console.log(`📊 Generated ${formattedTransfers.length} demo Solana transfers`);
    }
    
    // Update cache
    this.cache.set(cacheKey, {
      data: formattedTransfers,
      timestamp: Date.now()
    });
    
    return formattedTransfers;
  }

  /**
   * Get transfers for a specific Solana address
   */
  async getTransfersByAddress(
    address: string,
    page: number = 1,
    limit: number = 50
  ): Promise<FormattedSolTransfer[]> {
    const allTransfers = await this.getUSDPTransfers(page, limit);
    
    return allTransfers.filter(
      transfer =>
        transfer.from.toLowerCase() === address.toLowerCase() ||
        transfer.to.toLowerCase() === address.toLowerCase()
    );
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ Solscan cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Get last error message
   */
  getLastError(): string | null {
    return this.lastError;
  }

  /**
   * Test API connection
   */
  async testConnection(): Promise<{ success: boolean; message: string; sampleData?: any; error?: string; usingDemoData: boolean }> {
    try {
      console.log('🧪 Testing Solscan API connection...');
      
      if (this.useDemoData) {
        const demoTransfers = this.generateDemoTransfers(1);
        return {
          success: true,
          usingDemoData: true,
          message: 'Running in demo data mode (Solscan API has Cloudflare protection)',
          sampleData: demoTransfers[0]
        };
      }
      
      const transfers = await this.fetchTokenTransfers(this.usdpTokenAddress, 1, 1);
      
      if (transfers.length > 0) {
        return {
          success: true,
          usingDemoData: false,
          message: 'Solscan API connection successful',
          sampleData: transfers[0]
        };
      } else {
        return {
          success: true,
          usingDemoData: this.useDemoData,
          message: 'Solscan API connection successful but no transfers found for this token',
        };
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      return {
        success: false,
        usingDemoData: true,
        message: 'Solscan API connection failed',
        error: errorMsg
      };
    }
  }

  /**
   * Generate demo transfers for testing/development
   * These represent realistic Solana USDP transfers
   */
  generateDemoTransfers(count: number = 10): FormattedSolTransfer[] {
    const tokenConfig = getTokenConfig('USDP');
    const demoAddresses = [
      '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
      '5ZxZJ7P3mX9fQw8pRtZ9yQ2vK3mN8sL4tY6uI9oP0aS',
      '3MvN9pQwRt5yZ2xJ7kL4mN6sP8tY0uI2oP5aS7dF9gH',
      '9KjL8pQwRt5yZ2xJ3mN6sP4tY7uI0oP2aS5dF8gH1jK',
      '2NpR4jRn5sT6uV7wX8yZ9aB0cD1eF2gH3iJ4kL5mN6oP',
      '8QrS5tUv6Wx7Yz8Ab9Cd0Ef1Gh2Ij3Kl4Mn5Op6Qr7sT'
    ];
    
    // Generate realistic amounts
    const generateAmount = () => {
      const amounts = [100, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
      return amounts[Math.floor(Math.random() * amounts.length)].toString();
    };
    
    return Array.from({ length: count }, (_, i) => {
      const fromIndex = Math.floor(Math.random() * demoAddresses.length);
      let toIndex = Math.floor(Math.random() * demoAddresses.length);
      while (toIndex === fromIndex) {
        toIndex = Math.floor(Math.random() * demoAddresses.length);
      }
      
      // Create realistic Solana transaction hashes (base58 encoded, 64-88 characters)
      const txHashChars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
      let txHash = '';
      for (let j = 0; j < 64; j++) {
        txHash += txHashChars[Math.floor(Math.random() * txHashChars.length)];
      }
      
      return {
        id: `sol_demo_${i}_${Date.now()}`,
        txHash: txHash,
        from: demoAddresses[fromIndex],
        to: demoAddresses[toIndex],
        amount: generateAmount(),
        tokenSymbol: 'USDP',
        tokenName: 'USD.P E-Pay Dollar (Solana)',
        tokenLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
        blockNumber: 300000000 + i,
        timestamp: Date.now() - (i * 120000), // Each 2 minutes apart
        gasUsed: '0.000005',
        gasPrice: '0.000005',
        status: Math.random() > 0.05 ? 'confirmed' : 'failed', // 95% success rate
        chainId: this.chainId,
        networkName: 'Solana',
        type: 'transfer',
        slot: 300000000 + i
      };
    });
  }
}

// Export singleton instance
export const solscanService = SolscanService.getInstance();
